import { useQuery } from "@tanstack/react-query";

import type {
	ApiProblemDetails,
	InvestmentQuestionnaireResponse,
} from "Infrastructure/Api/Api";
import { getClientsClientIdInvestmentQuestionnaire } from "Infrastructure/Api/Api";
import keys from "../keys";

type QuestionnaireQueryResult = {
	data: InvestmentQuestionnaireResponse | null;
	status: 200 | 400 | 404 | 422 | undefined;
	errorMessage: string | null;
};

const extractErrorMessage = (data: any): string | null => {
	const exception = data as ApiProblemDetails;

	// Priority 1: Extract error message from errors dictionary
	// This handles enum-based errors like InvestmentQuestionnaireError
	if (exception?.errors && Object.keys(exception.errors).length > 0) {
		const firstError = Object.values(exception.errors)[0];
		if (firstError?.[0]?.message) {
			return firstError[0].message; // Returns enum value like "QuestionnaireExpired"
		}
	}

	// Priority 2: Fallback to title field
	if (exception?.title && exception.title.length > 0) {
		return exception.title;
	}

	return null;
};

export const useInvestmentQuestionnaireQuery = (
	clientId: number | null | undefined,
) => {
	return useQuery({
		queryKey: keys.investmentQuestionnaire(clientId),
		queryFn: async (): Promise<QuestionnaireQueryResult> => {
			if (!clientId)
				return { data: null, status: undefined, errorMessage: null };

			const response =
				await getClientsClientIdInvestmentQuestionnaire(clientId);

			// Handle 200 - success
			if (response.status === 200) {
				return {
					data: response.data as InvestmentQuestionnaireResponse,
					status: 200,
					errorMessage: null,
				};
			}

			// Handle 404 - not found
			if (response.status === 404) {
				return {
					data: null,
					status: 404,
					errorMessage: null,
				};
			}

			// Handle 400 - validation/business error with message
			if (response.status === 400) {
				return {
					data: null,
					status: 400,
					errorMessage: extractErrorMessage(response.data),
				};
			}

			// Handle 422 - business rule violation (e.g., questionnaire expired)
			if (response.status === 422) {
				return {
					data: null,
					status: 422,
					errorMessage: extractErrorMessage(response.data),
				};
			}

			// Handle other errors (500, network, etc.)
			throw new Error(
				`Failed to fetch investment questionnaire: ${response.status}`,
			);
		},
		enabled: !!clientId,
	});
};
